import { ListEmptyState, ListFilterStatus, useListFilters } from '../list-filter-feedback';
import { useUnsavedActions } from '../../general/unsaved-changes-context';
import '../../assets/entity-list.css';
import { useContentPageNavigation } from '../content-page';
import { useEffect, useState, useContext, useRef } from "react";
import { USER_GROUP_DRAG_TYPE } from '../../general/user-group-drag';
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { ExplainedButton as Button } from '../explained-button';
import { createPager, Pager, UserDto } from "../../general/dtos";
import { Api } from '../../general/api';
import { Dropdown } from 'primereact/dropdown';
import { createEmptyFilter, createEmptyOrderBy, DataTableFilter, DataTableOrderBy, getPaginatorLayout, getPaginatorOptions } from '../../general/data-grid-utils';
import { GeneralErrorDialog } from '../dialogs/error-dialog';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AxiosError } from 'axios';
import Config from '../../general/config';
import { useApi } from '../../providers/api-provider';
import { UserManagementDataContext, UserManagementDataContextValue } from '../../providers/user-management-provider';

interface UserListProps {
    selectedUsers: UserDto[];
    onSelectionChange: (users: UserDto[]) => void;
    fetchAll?: boolean;
    groupId?: number | 'ungrouped' | null;
    refreshKey?: number;
    allowGroupDrag?: boolean;
}

export function UserList({ selectedUsers, onSelectionChange, fetchAll = false, groupId = null, refreshKey = 0, allowGroupDrag = false }: UserListProps) {
    const { requestAction } = useUnsavedActions();
    const { showDetails } = useContentPageNavigation();
    const ctx: UserManagementDataContextValue | null = useContext<UserManagementDataContextValue | null>(UserManagementDataContext);
    const { startEditUser, cancelEditing } = ctx!;
    const api: Api = useApi(); 
    const dragStartedOnControl = useRef(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [users, setUsers] = useState<UserDto[]>([]);
    const [pager, setPager] = useState<Pager>(createPager(1, fetchAll ? 1000 : 10, 0));

    const scope = `${groupId ?? 'all'}:${refreshKey}`;
    const [pagerScope, setPagerScope] = useState(scope);
    const currentPage = pagerScope === scope ? pager.page : 1;

    const [filters, setFilters] = useState<DataTableFilter>(createEmptyFilter());

    const listFilters = useListFilters({ first_name: 'startsWith', last_name: 'startsWith', email: 'startsWith', is_admin: 'equals', last_logged_in: 'startsWith' });
    const clearFilters = () => {
        listFilters.clear();
        setPendingFilterBy('');
        setPendingFilterValue('');
        if (filters.filterBy && filters.filterValue !== '') {
            setPager(previous => ({ ...previous, page: 1 }));
            setFilters(createEmptyFilter());
        }
    };
    const [pendingFilterBy, setPendingFilterBy] = useState<string>("");
    const [pendingFilterValue, setPendingFilterValue] = useState<string>("");

    const [sorting, setSorting] = useState<DataTableOrderBy>(createEmptyOrderBy());

    const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);
    useEffect(() => { setLastClickedIndex(null); }, [scope]);

    const [error, setError] = useState<string>('');
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [errorDetails, setErrorDetails] = useState<string>('');

    useEffect(() => {
        if (!api) return;
        let active = true;
        setLoading(true);
        const args = [currentPage, pager.pageSize, sorting.orderBy, sorting.orderDirection, filters.filterBy, filters.filterValue] as const;
        const request = groupId === null ? api.getUsers(...args) : api.getUsers(...args, groupId);
        request.then(json => {
            if (!active) return;
            setUsers(json.data); setPager(json.pager); setPagerScope(scope);
        }).catch((failure: any) => {
            if (!active) return;
            setUsers([]);
            setError(failure.response?.data?.message ?? 'Could not load users.');
            setErrorTitle('Error loading users');
            setErrorDetails('Please try again.');
        }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
        // Query values identify requests; the API provider is stable once initialized.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pager.pageSize, sorting.orderBy, sorting.orderDirection, filters.filterBy, filters.filterValue, scope, !!api]);

    // --- paging / sorting / filtering ---
    const onPageChange = (e: DataTablePageEvent) => {
        let newPageSize: number | string = e.rows;
        const newPage: number = (e.page ?? 0) + 1; // safe default
        setPagerScope(scope);
        setPager({
            ...pager,
            page: newPage,
            pageSize: newPageSize
        });
    };

    const onSort = (e: any) => {
        setSorting({
            orderBy: e.sortField,
            orderDirection: e.sortOrder === 1 ? "asc" : "desc"
        });
    };

    // --- selection logic ---
    const rowClassName = (row: UserDto) =>
        selectedUsers.some(u => u.sub === row.sub) ? "row-selected" : "";

    // --- selection toolbar ---
    const selectAll = () => requestAction(() => {
        cancelEditing();
        onSelectionChange(users);
    });

    const selectNone = () => requestAction(() => {
        cancelEditing();
        onSelectionChange([]);
    });

    const invertSelection = () => requestAction(() => {
        const selectedSubs = new Set(selectedUsers.map(u => u.sub));
        const inverted = users.filter(u => !selectedSubs.has(u.sub));
        cancelEditing();
        onSelectionChange(inverted);
    });

    const handleRowClick = (event: any) => requestAction(() => {
        const row: UserDto = event.data;
        const rowIndex = users.findIndex(u => u.sub === row.sub);
        const isSelected = selectedUsers.some(u => u.sub === row.sub);

        const isCtrl = event.originalEvent.ctrlKey || event.originalEvent.metaKey;
        const isShift = event.originalEvent.shiftKey;

        // --- SHIFT CLICK: select range ---
        if (isShift && lastClickedIndex !== null) {
            const start = Math.min(lastClickedIndex, rowIndex);
            const end = Math.max(lastClickedIndex, rowIndex);

            const range = users.slice(start, end + 1);

            // merge with existing selection
            const merged = [
                ...selectedUsers.filter(u => !range.some(r => r.sub === u.sub)),
                ...range
            ];

            cancelEditing();
            onSelectionChange(merged);
            return;
        }

        // --- CTRL CLICK: toggle selection ---
        if (isCtrl) {
            cancelEditing();
            if (isSelected) {
                onSelectionChange(selectedUsers.filter(u => u.sub !== row.sub));
            } else {
                onSelectionChange([...selectedUsers, row]);
            }
            setLastClickedIndex(rowIndex);
            return;
        }

        // --- NORMAL CLICK: single select ---
        cancelEditing();
        onSelectionChange([row]);
        setLastClickedIndex(rowIndex);
        showDetails();
    });

    const getInitials = (user: UserDto) => {
        const first = user.first_name?.[0] ?? "";
        const last = user.last_name?.[0] ?? "";
        return (first + last).toUpperCase();
    };

    const buildMailto = (user: UserDto) => {
        const displayName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
        const subject = encodeURIComponent(`Message from ${Config.app_name} Admin`);
        const toField = encodeURIComponent(`${displayName} <${user.email}>`);
        const body = encodeURIComponent(
            `Hi ${user.first_name ?? 'there'},

I hope you're doing well.

I wanted to reach out to you regarding...

Best regards,
${Config.app_name} Admin`
        );

        return `mailto:${toField}?subject=${subject}&body=${body}`;
    };

    const isAdminFilter = pendingFilterBy === "is_admin";
    const rowsPerPageOptions = getPaginatorOptions(pager);
    const paginatorLayout = getPaginatorLayout(pager);
    const hasServerFilter = !!filters.filterBy && filters.filterValue !== '';
    const filterCount = listFilters.count + (hasServerFilter ? 1 : 0);
    const isAllDataLoaded: boolean = !hasServerFilter && users?.length >0 && users?.length >= pager.totalRows;

    return (<>
        <div className="entity-list-container">
            <div className="entity-list-toolbar admin-toolbar">
                <div className="selection-controls">
                    <Button label="All" onClick={selectAll} className="p-button-text p-button-sm" />
                    <Button label="None" onClick={selectNone} className="p-button-text p-button-sm" />
                    <Button label="Invert" onClick={invertSelection} className="p-button-text p-button-sm" />
                </div>

                <div className={`filter-controls${isAllDataLoaded ? ' filter-controls-inactive' : ''}`} style={{ visibility: isAllDataLoaded ? 'hidden' : 'visible' }}>
                    <span className="filter-title">Filter:</span>
                    <Dropdown
                        aria-label="User filter field"
                        value={pendingFilterBy}
                        options={[
                            { label: "None", value: "None" },
                            { label: "First name", value: "first_name" },
                            { label: "Last name", value: "last_name" },
                            { label: "Email", value: "email" },
                            { label: "Admin", value: "is_admin" }
                        ]}
                        onChange={(e) => {
                            const oldField: string = pendingFilterBy;
                            const newField: string = e.value;
                            setPendingFilterBy(newField);   

                            if (newField === "None") {
                                setPendingFilterValue("");
                                clearFilters();
                                return;
                            }
                            if (newField === "is_admin") {
                                setPendingFilterValue("0");
                            } else {
                                if (oldField === "is_admin") {
                                    setPendingFilterValue("");
                                }
                            }
                        }}
                        placeholder="Field"
                        className="filter-field"
                    />

                    {isAdminFilter ? (
                        <div className="filter-checkbox-wrapper">
                            <Checkbox aria-label="Filter administrators" checked={pendingFilterValue === "1"} onChange={(e) => { setPendingFilterValue(e.checked ? "1" : "0");}} />
                        </div>
                    ) : (
                        <InputText
                            value={pendingFilterValue}
                            onChange={(e) => setPendingFilterValue(e.target.value)}
                            placeholder="Search substring..."
                            className="filter-input"
                            disabled={!pendingFilterBy || pendingFilterBy === "None"}
                        />
                    )}

                    <Button
                        label="Apply" className="p-button-text p-button-sm" disabled={!pendingFilterBy || pendingFilterBy === "None"}
                        disabledReason="Choose a filter field first."
                        onClick={() => {
                            setPager(previous => ({ ...previous, page: 1 }));
                            setFilters({ filterBy: pendingFilterBy, filterValue: pendingFilterValue });
                        }}
                    />
                </div>
            </div>

            <ListFilterStatus count={filterCount} onClear={clearFilters} />
            <div className="entity-list-wrapper">
            <DataTable
                lazy={!isAllDataLoaded}
                filterDisplay={isAllDataLoaded ? "menu" : undefined}
                filters={listFilters.filters}
                onFilter={event => listFilters.setFilters(event.filters)}
                dataKey="sub"
                selection={selectedUsers}
                onRowClick={handleRowClick}
                loading={loading}
                pt={allowGroupDrag ? { bodyRow: {
                    draggable: !loading,
                    className: 'user-group-draggable-row',
                    onMouseDownCapture: (event: React.MouseEvent<HTMLTableRowElement>) => {
                        dragStartedOnControl.current = event.target instanceof Element && !!event.target.closest('button, a, input, textarea, select, [role="button"]');
                        if (!dragStartedOnControl.current && !loading) {
                            // Keep PrimeReact's row-reordering handler from disabling this native drag.
                            event.currentTarget.draggable = true;
                            event.stopPropagation();
                        }
                    },
                    onDragStartCapture: (event: React.DragEvent<HTMLTableRowElement>) => {
                        event.stopPropagation();
                        const key = event.currentTarget.querySelector<HTMLElement>('[data-user-key]')?.dataset.userKey;
                        const row = users.find(user => user.sub === key);
                        if (!row || loading || dragStartedOnControl.current) { event.preventDefault(); return; }
                        const dragged = selectedUsers.some(user => user.sub === key) ? selectedUsers : [row];
                        window.getSelection()?.removeAllRanges();
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData(USER_GROUP_DRAG_TYPE, JSON.stringify(dragged.map(user => user.sub)));
                        event.dataTransfer.setData('text/plain', dragged.map(user => `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email).join(', '));
                    },
                } } : undefined}

                selectionMode="multiple"
                // metaKeySelection={false}
                // onSelectionChange={(e) => onSelectionChange(e.value)}
                
                value={users}
                paginator
                rows={pager.pageSize}
                totalRecords={pager.totalRows}
                first={(currentPage - 1) * pager.pageSize}
                onPage={onPageChange}
                sortField={sorting.orderBy}
                sortOrder={sorting.orderDirection === "asc" ? 1 : -1}
                onSort={isAllDataLoaded ? undefined : onSort}
                removableSort
                scrollable
                scrollHeight="flex"
                // rowClassName={rowClassName}
                paginatorTemplate={paginatorLayout}
                rowsPerPageOptions={rowsPerPageOptions}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                emptyMessage={<ListEmptyState filtered={filterCount > 0} empty={groupId === null ? "No users yet." : groupId === 'ungrouped' ? "All users belong to a group." : "This group has no users. Move users here from All users."} noMatches="No users match these filters." onClear={clearFilters} />}
            >
                <Column header="" style={{ width: "3rem" }}
                    body={(row: UserDto) => (
                        row.picture ? (
                            <img
                                src={row.picture}
                                alt={`${row.first_name} ${row.last_name}`}
                                title={`${row.first_name} ${row.last_name}`}
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    display: "block",
                                    margin: "0 auto"
                                }}
                                onError={(e) => { e.currentTarget.src = ""; }} // triggers fallback
                            />
                        ) : (
                            <div
                                title={`${row.first_name} ${row.last_name}`}
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    backgroundColor: "#ccc",
                                    color: "#333",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    margin: "0 auto"
                                }}
                            >
                                {getInitials(row)}
                            </div>
                        )
                    )}
                />
                <Column field="first_name" header="First name" sortable filter={isAllDataLoaded} body={(row: UserDto) => <span data-user-key={row.sub}>{row.first_name}</span>}/>
                <Column field="last_name" header="Last name" sortable filter={isAllDataLoaded}/>
                <Column field="email" header="Email" sortable filter={isAllDataLoaded}
                    body={(row: UserDto) => (
                        <a
                            href={buildMailto(row)}
                            onClick={(e) => e.stopPropagation()} // don’t trigger row selection
                            style={{ color: "#0078d4", textDecoration: "underline", cursor: "pointer" }}
                            title={`Email ${row.first_name} ${row.last_name}`}
                        >
                            {row.email}
                        </a>
                    )}
                />
                <Column field="is_admin" header="Admin" dataType='boolean' sortable  filter={isAllDataLoaded} body={(row) => (row.is_admin ? "Yes" : "")}/>
                <Column field="last_logged_in" header="Last login" sortable filter={isAllDataLoaded} bodyStyle={{ whiteSpace: 'nowrap' }}/>
                <Column header="" body={(row: UserDto) => (
                    <span className="admin-row-actions">
                        <Button icon="pi pi-pencil" onClick={() => requestAction(() => {
                            onSelectionChange([row]);
                            startEditUser(row.sub);
                            showDetails();
                        })} tooltip='Edit user' aria-label="Edit user" />
                    </span>
                    )}
                />
            </DataTable>
            </div>
        </div>

        <GeneralErrorDialog
            visible={error.length > 0}
            title={errorTitle}
            message={error}
            details={errorDetails}
            onClose={() => setError('')}
        />
    </>);
}
